import '@theme/quick-add';

/**
 * Opens the quick add dialog instead of adding to cart when the add-to-cart
 * button inside a product grid card belongs to a product with more than one
 * variant (marked with the `quick-view-trigger` class in buy-buttons.liquid).
 *
 * Scoped to `product-card` so the main product page form and the form inside
 * the quick add modal keep their default behavior.
 *
 * Listeners are attached to `window` in the capture phase: the theme's
 * component framework delegates all `on:*` events on `document` (capture),
 * and window capture listeners run before those. Stopping propagation here
 * prevents the theme's add-to-cart click handling entirely.
 */

const QUICK_VIEW_BUTTON = 'product-card .quick-view-trigger button[type="submit"][name="add"]';

/**
 * @param {Element} target
 * @returns {Element | null} The product card containing a quick-view-trigger form, or null.
 */
function getTriggeredProductCard(target) {
  const trigger = target.closest('product-card .quick-view-trigger');
  return trigger ? trigger.closest('product-card') : null;
}

// The Zipify OCU cart drawer listens for `touchend` on `document` in the capture
// phase, so on touch devices it runs its add-to-cart before a click event is
// even synthesized — adding the default variant and closing the quick view the
// moment it opens. Stop the touch events for these buttons before they reach
// the document. Deliberately no `preventDefault()`: the click that the browser
// synthesizes afterwards is what the handler below relies on.
for (const type of ['touchstart', 'touchend']) {
  window.addEventListener(
    type,
    (event) => {
      if (!(event.target instanceof Element)) return;
      if (!event.target.closest(QUICK_VIEW_BUTTON)) return;
      if (!document.getElementById('quick-add-dialog')) return;

      event.stopImmediatePropagation();
    },
    { capture: true }
  );
}

window.addEventListener(
  'click',
  (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest(QUICK_VIEW_BUTTON);
    if (!button) return;

    // Without the dialog in the DOM the quick view can't open — keep the default add-to-cart behavior.
    if (!document.getElementById('quick-add-dialog')) return;

    const productCard = button.closest('product-card');
    if (!productCard) return;

    // Block the submit-button default action and stop the event before it
    // reaches the document, where the theme's delegated click handlers
    // (add-to-cart animation, view transition) would otherwise run.
    event.preventDefault();
    event.stopImmediatePropagation();

    let quickAdd = productCard.querySelector('quick-add-component');

    if (!quickAdd) {
      quickAdd = document.createElement('quick-add-component');
      quickAdd.setAttribute('hidden', '');

      const productTitle = productCard.querySelector('.product-card__link .visually-hidden');
      if (productTitle?.textContent) {
        quickAdd.setAttribute('data-product-title', productTitle.textContent.trim());
      }

      productCard.appendChild(quickAdd);
    }

    if (typeof (/** @type {any} */ (quickAdd).handleClick) === 'function') {
      /** @type {any} */ (quickAdd).handleClick(event);
    }
  },
  { capture: true }
);

// Safety net: if a submit still gets through (e.g. Enter key inside the form),
// block it for quick-view-trigger forms in product cards so nothing is added to cart.
window.addEventListener(
  'submit',
  (event) => {
    if (!(event.target instanceof Element)) return;
    if (!document.getElementById('quick-add-dialog')) return;

    if (getTriggeredProductCard(event.target)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  },
  { capture: true }
);
